import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export type RatioUnit = {
  key: string;
  label: string;
  count: number;
};

interface RatioUnitSelectorProps {
  units: RatioUnit[];
  activeKey: string;
  onChange: (key: string) => void;
  maxVisible?: number;
}

const RatioUnitSelector: React.FC<RatioUnitSelectorProps> = ({
  units,
  activeKey,
  onChange,
  maxVisible = 3,
}) => {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const { visible, overflow } = useMemo(() => {
    const sorted = [...units].sort((a, b) => b.count - a.count);
    const top = sorted.slice(0, maxVisible);
    const rest = sorted.slice(maxVisible);
    const topHasActive = top.some(u => u.key === activeKey);
    if (!topHasActive) {
      const activeUnit = units.find(u => u.key === activeKey);
      if (activeUnit) {
        const demoted = top.pop();
        top.push(activeUnit);
        if (demoted) rest.unshift(demoted);
      }
    }
    return { visible: top, overflow: rest };
  }, [units, activeKey, maxVisible]);

  const handleSelect = (key: string, count: number) => {
    if (count === 0) return;
    onChange(key);
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm font-medium text-gray-600 mr-1">Référentiel :</span>
      {visible.map((unit) => {
        const active = unit.key === activeKey;
        const disabled = unit.count === 0;
        return (
          <button
            key={unit.key}
            onClick={() => handleSelect(unit.key, unit.count)}
            disabled={disabled}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              active
                ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                : disabled
                ? 'bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed'
                : 'bg-white text-gray-700 border-gray-300 hover:border-violet-400 hover:text-violet-700'
            }`}
          >
            {unit.label}
            <span className={`ml-1.5 text-xs ${active ? 'text-violet-100' : 'text-gray-400'}`}>
              ({unit.count})
            </span>
          </button>
        );
      })}
      {overflow.length > 0 && (
        <div className="relative" ref={popoverRef}>
          <button
            onClick={() => setOpen(o => !o)}
            className="px-3 py-1.5 text-sm rounded-full border border-dashed border-gray-300 text-gray-600 hover:border-violet-400 hover:text-violet-700 transition-colors flex items-center gap-1"
          >
            + {overflow.length} autre{overflow.length > 1 ? 's' : ''}
            <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {open && (
            <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[220px]">
              {overflow.map(unit => {
                const disabled = unit.count === 0;
                const active = unit.key === activeKey;
                return (
                  <button
                    key={unit.key}
                    onClick={() => handleSelect(unit.key, unit.count)}
                    disabled={disabled}
                    className={`w-full flex items-center justify-between px-3 py-2 text-sm ${
                      disabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : active
                        ? 'bg-violet-50 text-violet-700'
                        : 'text-gray-700 hover:bg-violet-50 hover:text-violet-700'
                    }`}
                  >
                    <span>{unit.label}</span>
                    <span className="text-xs text-gray-400">({unit.count})</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RatioUnitSelector;
